import React from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import EquipmentInspectionForm from '../../forms/EquipmentInspectionForm';
import { api, getApiErrorMessage } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { useAuth } from '../../contexts/AuthContext';
import { isResumableDraft, inspectionFormTypeLabel } from '../../lib/inspection-labels';
import { inspectionDetailPath } from '../../lib/inspection-surface';

/**
 * Reopening one of your own unfinished inspections.
 *
 * THE SPINNER IS LOAD-BEARING, NOT POLITENESS. Almost every field in
 * `EquipmentInspectionForm` is a `useState` initialiser, so a row that arrives
 * on a later render is never read: the form would mount blank, and the first
 * save would write that blank over the hand's work. `existing` is therefore
 * only ever handed to a form that has not mounted yet, which is what this
 * component is for. Same rule as the draft-gated PhotoUploader — a form must
 * not be interactive before the record behind it resolves.
 *
 * THREE REFUSALS, ALL DELIBERATE, NONE OF THEM AN EMPTY STATE:
 *   - a failed read says so, and does not offer a blank form. Handing someone a
 *     fresh form because their draft could not be fetched is how one inspection
 *     becomes two.
 *   - a SUBMITTED row redirects to the read-only record. A filed inspection is
 *     signed, and reopening it in an editor is a different feature from the one
 *     that was asked for.
 *   - someone ELSE'S row redirects to the record too. This screen is "your
 *     own". It is a display rule and not a security claim: the API's PATCH
 *     checks tenancy and not ownership, which is reported separately and is not
 *     fixed here.
 */
export default function FieldInspectionResumeScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['casing-inspection-submission', id],
    queryFn: () => api.casingInspections.get(id!).then((r: any) => unwrap<any>(r.data)),
    enabled: !!id,
    // The form writes back to this row; a cached copy from the list would seed
    // it with values a later save has already changed.
    staleTime: 0,
  });

  if (isLoading) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark">
        <div style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <PageHeader title="Equipment Inspection" />
        </div>
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-fieldiq-text" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark">
        <div style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <PageHeader title="Equipment Inspection" />
        </div>
        <div className="px-4 py-4">
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {isError
              ? getApiErrorMessage(error, 'Could not open that inspection.')
              : 'That inspection could not be found.'}
          </div>
        </div>
      </div>
    );
  }

  const ownerId = data.submitted_by ?? data.submittedBy ?? null;
  const isOwn = !!user?.id && !!ownerId && String(ownerId) === String(user.id);

  if (!isResumableDraft(data) || !isOwn) {
    return <Navigate to={inspectionDetailPath('/field', String(id))} replace />;
  }

  return (
    <div className="animate-fade-in">
      <div style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <PageHeader title={`${inspectionFormTypeLabel(data)} Inspection`} />
      </div>
      <div className="px-4 py-4">
        <EquipmentInspectionForm existing={data} onCancel={() => navigate(-1)} />
      </div>
    </div>
  );
}
